// Vendor
import * as React from 'react';
import {
  SPOT_BRAND,
  SPOT_CLEANOUT,
  SPOT_CORAL_LIGHT,
  SPOT_WHITE,
  SPOT_YELLOW
} from '@thredup/styleguide/src/variables/colors';

// Internal
import { Image } from '@src/components/Image/Image';
import { DEFAULT_SAL_PROPS, SAL_DELAY } from '@src/config/sal';

const ResaleMarketMassive: React.FC = () => {
  return (
    <div className="tup-slide">
      <h1 {...DEFAULT_SAL_PROPS} className="max-w-xl">
        Resale Market is Massive, and It’s Just Getting Started
      </h1>
      <h2
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={SAL_DELAY * 1}
        className="max-w-lg mb-8"
      >
        82% of America hasn’t yet resold clothing, but most consumers are open
        to doing so.<sup>2</sup>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 md:gap-32">
        <div className="col-span-2">
          <div
            {...DEFAULT_SAL_PROPS}
            data-sal="fade"
            data-sal-delay={SAL_DELAY * 4}
          >
            <Image alt="Pie Chart" fileName="2b-pieChart.jpg" />
          </div>
          <div className="flex items-baseline justify-between md:p-6 pt-4">
            <div
              {...DEFAULT_SAL_PROPS}
              data-sal="fade"
              data-sal-delay={SAL_DELAY * 5}
              className="text-center"
              style={{ maxWidth: 140 }}
            >
              <p className="text-cleanout-light font-bold text-4xl leading-none">
                18%
              </p>
              <p className="text-xs md:text-sm">
                of people have ever
                <br />
                resold clothing.
              </p>
            </div>
            <div
              {...DEFAULT_SAL_PROPS}
              data-sal="fade"
              data-sal-delay={SAL_DELAY * 6}
              className="text-center"
              style={{ maxWidth: 140 }}
            >
              <p className="text-brand font-bold text-4xl leading-none">67%</p>
              <p className="text-xs md:text-sm">
                of people who haven’t are open to reselling their clothing.
              </p>
            </div>
          </div>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal="fade"
          data-sal-delay={SAL_DELAY * 6}
          className="text-center bg-gray-0 pb-4"
        >
          <Image
            alt="Woman with thredUP cleanout kit"
            fileName="2b-cleanout.jpg"
          />
          <p className="p-8 pt-4 pb-0">
            <b> 70%</b> of sellers say they’ll definitely do it again.
            <sup>10</sup>
          </p>
        </div>
      </div>
      <h2
        {...DEFAULT_SAL_PROPS}
        data-sal="fade"
        data-sal-delay={SAL_DELAY * 6}
        className="mb-4 mt-12"
      >
        Top Reasons Why People Sell Their Clothes<sup>2</sup>
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={SAL_DELAY * 3}
          className="text-center"
        >
          <svg
            className="m-auto"
            width="49px"
            height="55px"
            viewBox="0 0 49 55"
            version="1.1"
          >
            <title>Cash</title>
            <desc>Cash Icon</desc>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g id="Group-11">
                <path
                  d="M14.1352,0.7496 L34.6472,0.7496 C33.8172,4.5926 29.6692,7.7026 24.3912,7.7026 C19.1122,7.7026 14.9652,4.5926 14.1352,0.7496 Z"
                  id="Stroke-1"
                  stroke={SPOT_YELLOW}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M21.1528,7.399 C22.1948,7.621 23.2778,7.747 24.3908,7.747 C25.5188,7.747 26.6178,7.627 27.6718,7.399 C27.7988,7.511 27.9468,7.6 28.1058,7.66 C45.2818,13.709 42.4598,28.079 45.3058,37.577 C46.0308,40.717 48.0158,41.721 48.0318,43.903 C48.0318,45.738 47.1198,47.132 45.4018,48.444 C43.6848,49.755 41.1938,50.837 38.4928,51.617 C33.0898,53.176 26.8528,53.637 24.3908,53.637 C21.9278,53.637 15.6918,53.176 10.2888,51.617 C7.5878,50.837 5.0958,49.755 3.3798,48.444 C1.6618,47.132 0.7508,45.738 0.7508,43.903 C0.7508,41.643 2.6278,40.966 3.4508,37.658 C6.3388,28.161 3.4508,13.726 20.6748,7.66 C20.8488,7.605 21.0128,7.516 21.1528,7.399 Z"
                  id="Stroke-3"
                  stroke={SPOT_YELLOW}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M25.0629,38.9352 L25.0629,30.5352 C27.6409,31.2692 29.9909,32.1382 29.9909,34.8302 C29.9909,36.4612 28.9589,38.7172 25.0629,38.9352 L25.0629,38.9352 Z M24.1469,29.3382 C21.6259,28.6312 19.3909,27.8972 19.3909,25.6142 C19.3909,23.4662 21.4829,21.8892 24.1469,21.8072 L24.1469,29.3382 Z M25.0629,29.5822 L25.0629,21.8342 C26.9819,21.9712 28.6439,22.7052 29.8469,24.1722 L30.5629,23.5472 C29.3319,22.1072 27.4989,21.1552 25.0629,21.0192 L25.0629,18.5452 L24.1469,18.5452 L24.1469,20.9922 C21.0829,21.0742 18.3899,22.7592 18.3899,25.6142 C18.3899,28.7132 21.2529,29.5292 24.1469,30.2892 L24.1469,38.9352 C21.5409,38.8272 19.6499,37.5482 18.5319,36.2442 L17.7889,36.8692 C19.1059,38.4732 21.3109,39.6692 24.1469,39.7502 L24.1469,42.1432 L25.0629,42.1432 L25.0629,39.7232 C29.7329,39.5052 30.9939,36.7882 30.9939,34.8302 C30.9939,31.4052 28.0139,30.3992 25.0629,29.5822 L25.0629,29.5822 Z"
                  id="Fill-5"
                  fill={SPOT_YELLOW}
                ></path>
                <path
                  d="M25.0629,38.9352 L25.0629,30.5352 C27.6409,31.2692 29.9909,32.1382 29.9909,34.8302 C29.9909,36.4612 28.9589,38.7172 25.0629,38.9352 Z M24.1469,29.3382 C21.6259,28.6312 19.3909,27.8972 19.3909,25.6142 C19.3909,23.4662 21.4829,21.8892 24.1469,21.8072 L24.1469,29.3382 Z M25.0629,29.5822 L25.0629,21.8342 C26.9819,21.9712 28.6439,22.7052 29.8469,24.1722 L30.5629,23.5472 C29.3319,22.1072 27.4989,21.1552 25.0629,21.0192 L25.0629,18.5452 L24.1469,18.5452 L24.1469,20.9922 C21.0829,21.0742 18.3899,22.7592 18.3899,25.6142 C18.3899,28.7132 21.2529,29.5292 24.1469,30.2892 L24.1469,38.9352 C21.5409,38.8272 19.6499,37.5482 18.5319,36.2442 L17.7889,36.8692 C19.1059,38.4732 21.3109,39.6692 24.1469,39.7502 L24.1469,42.1432 L25.0629,42.1432 L25.0629,39.7232 C29.7329,39.5052 30.9939,36.7882 30.9939,34.8302 C30.9939,31.4052 28.0139,30.3992 25.0629,29.5822 Z"
                  id="Stroke-7"
                  stroke={SPOT_YELLOW}
                  strokeWidth="0.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M24.391,7.7467 C24.391,7.7467 33.507,6.3307 39.274,8.9367"
                  id="Stroke-9"
                  stroke={SPOT_YELLOW}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </g>
            </g>
          </svg>
          <p>
            Make extra
            <br />
            cash
          </p>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={SAL_DELAY * 4}
          className="text-center"
        >
          <svg
            className="m-auto"
            width="38px"
            height="57px"
            viewBox="0 0 38 57"
            version="1.1"
          >
            <title>Leaf</title>
            <desc>Leaf icon</desc>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g
                id="Group-13"
                transform="translate(1.000000, 1.000000)"
                stroke={SPOT_BRAND}
                strokeWidth="1.744"
              >
                <path
                  d="M16.1941578,49 L19.8058422,49 C28.8855986,48.0531509 36,40.0479725 36,30.3424385 C36,16.6310046 19.9239999,1.14902912 19.2399985,0.497491024 C18.53893,-0.173911042 17.4334999,-0.164641191 16.7363699,0.518679254 C16.0549943,1.19140558 0,17.1620346 0,30.3424385 C0,40.0479725 7.11308851,48.0531509 16.1941578,49 Z"
                  id="Stroke-1"
                ></path>
                <line
                  x1="18.5"
                  y1="0"
                  x2="18.5"
                  y2="55"
                  id="Stroke-3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></line>
                <line
                  x1="18"
                  y1="25"
                  x2="6"
                  y2="14"
                  id="Stroke-5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></line>
                <line
                  x1="18"
                  y1="42"
                  x2="1"
                  y2="26"
                  id="Stroke-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></line>
                <line
                  x1="18"
                  y1="25"
                  x2="30"
                  y2="14"
                  id="Stroke-9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></line>
                <line
                  x1="18"
                  y1="41"
                  x2="35"
                  y2="25"
                  id="Stroke-11"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></line>
              </g>
            </g>
          </svg>
          <p>
            Do good for the
            <br /> environment
          </p>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={SAL_DELAY * 5}
          className="text-center"
        >
          <svg
            className="m-auto"
            width="57px"
            height="63px"
            viewBox="0 0 57 63"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Clothes</title>
            <desc>Clothes icon</desc>
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g id="Group-13">
                <g id="Group-4">
                  <path
                    d="M28.1053043,5.84243902 C33.7856522,6.15341463 38.339913,0.665609756 38.9826957,0.757073171 C43.9910435,0.757073171 46.2334783,2.57292683 48.0461739,4.38878049 C48.5526087,4.89609756 55.7729565,12.2265854 55.2981739,12.5607317 C55.2957391,12.5619512 47.1404348,18.0095122 47.1404348,18.0095122 L44.4207826,15.2839024 L43.5990435,21.8729268 C43.3592174,23.785122 43.4858261,25.8192683 43.526,27.7473171 C43.5661739,29.6985366 40.6018261,49.7741463 41.1192174,49.787561 L28.1053043,49.787561 L28.1284348,49.787561 L14.8345217,49.787561 C15.351913,49.7741463 12.6663478,29.6985366 12.7077391,27.7473171 C12.747913,25.8192683 12.8733043,23.785122 12.6346957,21.8729268 L11.8117391,15.2839024 L9.09330435,18.0095122 C9.09330435,18.0095122 0.936782609,12.5619512 0.935565217,12.5607317 C0.460782609,12.2265854 7.68113043,4.89609756 8.18756522,4.38878049 C10.0002609,2.57292683 12.2426957,0.757073171 17.2510435,0.757073171 C17.8926087,0.665609756 22.3251304,6.15341463 28.0054783,5.84243902"
                    id="Fill-1"
                    fill={SPOT_WHITE}
                  ></path>
                  <path
                    d="M28.1053043,5.84243902 C33.7856522,6.15341463 38.339913,0.665609756 38.9826957,0.757073171 C43.9910435,0.757073171 46.2334783,2.57292683 48.0461739,4.38878049 C48.5526087,4.89609756 55.7729565,12.2265854 55.2981739,12.5607317 C55.2957391,12.5619512 47.1404348,18.0095122 47.1404348,18.0095122 L44.4207826,15.2839024 L43.5990435,21.8729268 C43.3592174,23.785122 43.4858261,25.8192683 43.526,27.7473171 C43.5661739,29.6985366 40.6018261,49.7741463 41.1192174,49.787561 L28.1053043,49.787561 L28.1284348,49.787561 L14.8345217,49.787561 C15.351913,49.7741463 12.6663478,29.6985366 12.7077391,27.7473171 C12.747913,25.8192683 12.8733043,23.785122 12.6346957,21.8729268 L11.8117391,15.2839024 L9.09330435,18.0095122 C9.09330435,18.0095122 0.936782609,12.5619512 0.935565217,12.5607317 C0.460782609,12.2265854 7.68113043,4.89609756 8.18756522,4.38878049 C10.0002609,2.57292683 12.2426957,0.757073171 17.2510435,0.757073171 C17.8926087,0.665609756 22.3251304,6.15341463 28.0054783,5.84243902"
                    id="Stroke-3"
                    stroke={SPOT_CORAL_LIGHT}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
                <g id="Group-8" transform="translate(0.000000, 7.000000)">
                  <path
                    d="M28.1053043,5.07470714 C33.7856522,5.38435 38.339913,-0.0799357143 38.9826957,0.0111357143 C43.9910435,0.0111357143 46.2334783,1.81799286 48.0461739,3.62727857 C48.5526087,4.13242143 55.7729565,11.4314929 55.2981739,11.7642071 C55.2957391,11.7654214 47.1404348,17.1896357 47.1404348,17.1896357 L44.4207826,14.4757071 L43.5990435,21.0364929 C43.3592174,22.9404929 43.4858261,24.9659214 43.526,26.8857071 C43.5661739,28.8285643 41.5075652,48.8181357 42.0261739,48.8314929 L28.1053043,48.8314929 L28.1284348,48.8314929 L13.9275652,48.8314929 C14.4449565,48.8181357 12.6663478,28.8285643 12.7077391,26.8857071 C12.747913,24.9659214 12.8733043,22.9404929 12.6346957,21.0364929 L11.8117391,14.4757071 L9.09330435,17.1896357 C9.09330435,17.1896357 0.936782609,11.7654214 0.935565217,11.7642071 C0.460782609,11.4314929 7.68113043,4.13242143 8.18756522,3.62727857 C10.0002609,1.81799286 12.2426957,0.0111357143 17.2510435,0.0111357143 C17.8926087,-0.0799357143 22.3251304,5.38435 28.0054783,5.07470714"
                    id="Fill-5"
                    fill={SPOT_WHITE}
                  ></path>
                  <path
                    d="M28.1053043,5.07470714 C33.7856522,5.38435 38.339913,-0.0799357143 38.9826957,0.0111357143 C43.9910435,0.0111357143 46.2334783,1.81799286 48.0461739,3.62727857 C48.5526087,4.13242143 55.7729565,11.4314929 55.2981739,11.7642071 C55.2957391,11.7654214 47.1404348,17.1896357 47.1404348,17.1896357 L44.4207826,14.4757071 L43.5990435,21.0364929 C43.3592174,22.9404929 43.4858261,24.9659214 43.526,26.8857071 C43.5661739,28.8285643 41.5075652,48.8181357 42.0261739,48.8314929 L28.1053043,48.8314929 L28.1284348,48.8314929 L13.9275652,48.8314929 C14.4449565,48.8181357 12.6663478,28.8285643 12.7077391,26.8857071 C12.747913,24.9659214 12.8733043,22.9404929 12.6346957,21.0364929 L11.8117391,14.4757071 L9.09330435,17.1896357 C9.09330435,17.1896357 0.936782609,11.7654214 0.935565217,11.7642071 C0.460782609,11.4314929 7.68113043,4.13242143 8.18756522,3.62727857 C10.0002609,1.81799286 12.2426957,0.0111357143 17.2510435,0.0111357143 C17.8926087,-0.0799357143 22.3251304,5.38435 28.0054783,5.07470714"
                    id="Stroke-7"
                    stroke={SPOT_CORAL_LIGHT}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
                <g id="Group-12" transform="translate(0.000000, 13.000000)">
                  <path
                    d="M28.1053043,5.13987805 C33.7856522,5.45085366 38.339913,-0.0369512195 38.9826957,0.0545121951 C43.9910435,0.0545121951 46.2334783,1.86914634 48.0461739,3.68621951 C48.5526087,4.19353659 55.7729565,11.5240244 55.2981739,11.8581707 C55.2957391,11.8593902 47.1404348,17.3069512 47.1404348,17.3069512 L44.4207826,14.5813415 L43.5990435,21.1703659 C43.3592174,23.0813415 43.4858261,25.1167073 43.526,27.0447561 C43.5661739,28.9947561 43.9033913,49.0715854 44.4195652,49.085 L28.1053043,49.085 L28.1284348,49.085 L11.8129565,49.085 C12.3303478,49.0715854 12.6663478,28.9947561 12.7077391,27.0447561 C12.747913,25.1167073 12.8733043,23.0813415 12.6346957,21.1703659 L11.8117391,14.5813415 L9.09330435,17.3069512 C9.09330435,17.3069512 0.936782609,11.8593902 0.935565217,11.8581707 C0.460782609,11.5240244 7.68113043,4.19353659 8.18756522,3.68621951 C10.0002609,1.86914634 12.2426957,0.0545121951 17.2510435,0.0545121951 C17.8926087,-0.0369512195 22.3251304,5.45085366 28.0054783,5.13987805"
                    id="Fill-9"
                    fill={SPOT_WHITE}
                  ></path>
                  <path
                    d="M28.1053043,5.13987805 C33.7856522,5.45085366 38.339913,-0.0369512195 38.9826957,0.0545121951 C43.9910435,0.0545121951 46.2334783,1.86914634 48.0461739,3.68621951 C48.5526087,4.19353659 55.7729565,11.5240244 55.2981739,11.8581707 C55.2957391,11.8593902 47.1404348,17.3069512 47.1404348,17.3069512 L44.4207826,14.5813415 L43.5990435,21.1703659 C43.3592174,23.0813415 43.4858261,25.1167073 43.526,27.0447561 C43.5661739,28.9947561 43.9033913,49.0715854 44.4195652,49.085 L28.1053043,49.085 L28.1284348,49.085 L11.8129565,49.085 C12.3303478,49.0715854 12.6663478,28.9947561 12.7077391,27.0447561 C12.747913,25.1167073 12.8733043,23.0813415 12.6346957,21.1703659 L11.8117391,14.5813415 L9.09330435,17.3069512 C9.09330435,17.3069512 0.936782609,11.8593902 0.935565217,11.8581707 C0.460782609,11.5240244 7.68113043,4.19353659 8.18756522,3.68621951 C10.0002609,1.86914634 12.2426957,0.0545121951 17.2510435,0.0545121951 C17.8926087,-0.0369512195 22.3251304,5.45085366 28.0054783,5.13987805"
                    id="Stroke-11"
                    stroke={SPOT_CORAL_LIGHT}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
              </g>
            </g>
          </svg>
          <p>
            Make money to
            <br />
            buy more clothes
          </p>
        </div>
        <div
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={SAL_DELAY * 6}
          className="text-center"
        >
          <svg
            className="m-auto"
            width="87px"
            height="48px"
            viewBox="0 0 87 48"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Hanger</title>
            <desc>Hanger icon</desc>
            <g
              stroke="none"
              strokeWidth="1"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <g
                id="2b"
                transform="translate(-1087.000000, -744.000000)"
                stroke={SPOT_CLEANOUT}
                strokeWidth="1.5"
              >
                <g id="Group-7" transform="translate(1088.000000, 745.000000)">
                  <path
                    d="M85,46 L46.468523,25.7242186 C45.2265503,25.0156526 43.8632752,24.6613696 42.5,24.6613696 C41.1367248,24.6613696 39.7734497,25.0156526 38.531477,25.7242186 L0,46 L0,45.7481269 C0,43.1615843 1.40738518,39.1745168 3.6721588,38.0466237 C13.2647087,33.2665712 25.4363162,26.813362 28.9444408,23.2137915 C31.207836,20.8915772 32.7999319,20.270198 33.6766184,18.0573133 C33.930251,17.4151754 34.5257362,17 35.1901434,17 L49.8098566,17 C50.4742638,17 51.069749,17.4151754 51.3233816,18.0586972 C52.2000681,20.270198 53.792164,20.8915772 56.0555592,23.2137915 C59.5650623,26.813362 71.7352913,33.2665712 81.3278412,38.0466237 C83.5926148,39.1745168 85,43.1602004 85,45.7481269 L85,46 Z"
                    id="Stroke-1"
                  ></path>
                  <line x1="9" y1="42.5" x2="78" y2="42.5" id="Stroke-3"></line>
                  <path
                    d="M35,4.44001722 C35.888001,2.38037672 37.7168105,0.675846656 39.9305593,0.156839103 C42.1443082,-0.363534259 44.6832405,0.430000973 45.9978711,2.25472226 C47.0456844,3.71067503 47.2444076,5.66514821 46.7218906,7.3724099 C46.197984,9.07967158 45.0098137,8.63305193 43.5645539,9.7106755 C42.7932854,10.2856812 41.8913877,10.8333708 41.5953874,11.7389024 C41.4550305,12.1718639 41.4730962,12.6389707 41.4939413,13.0951511 C41.5523076,14.3967674 41.6120635,15.6983837 41.6704298,17"
                    id="Stroke-5"
                  ></path>
                </g>
              </g>
            </g>
          </svg>
          <p>
            Make room in
            <br />
            their closet
          </p>
        </div>
      </div>
    </div>
  );
};

export { ResaleMarketMassive };
